/**
 * NoticesSection -- 안내사항 편집 (주차, 셔틀, 드레스코드 등)
 */

import { Bell, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { Input, Textarea, Select, Button } from '../../ui/index.js'
import type { NoticeItem } from '@cugumi/shared/schemas'
import styles from './SectionForm.module.css'

const ICON_OPTIONS = [
  { value: '', label: '선택 안함' },
  { value: 'parking', label: '주차' },
  { value: 'shuttle', label: '셔틀' },
  { value: 'meal', label: '식사' },
  { value: 'dress', label: '복장' },
  { value: 'gift', label: '축의금' },
  { value: 'photo', label: '촬영' },
  { value: 'etc', label: '기타' },
]

const ICON_LABEL_MAP: Record<string, string> = Object.fromEntries(
  ICON_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
)

export function NoticesSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  if (!invitation) return null

  const items: NoticeItem[] = (invitation as any).notices ?? []

  function updateItems(next: NoticeItem[]) {
    updateNested('notices', next)
  }

  function addItem() {
    updateItems([...items, { icon: '', title: '', description: '' }])
  }

  function removeItem(index: number) {
    updateItems(items.filter((_, i) => i !== index))
  }

  function updateItem(index: number, field: keyof NoticeItem, value: string) {
    updateItems(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)))
  }

  function moveItem(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= items.length) return
    const next = [...items]
    ;[next[index], next[target]] = [next[target], next[index]]
    updateItems(next)
  }

  return (
    <div className={styles.form}>
      <div className={styles.personCard}>
        <div className={styles.personCardHeader}>
          <span className={styles.personCardIconBadge}>
            <Bell size={20} />
          </span>
          <span className={styles.personCardLabel}>안내사항</span>
          <div className={styles.personCardHeaderActions}>
            <Button variant="ghost" size="sm" onClick={addItem}>
              <Plus size={14} /> 추가
            </Button>
          </div>
        </div>
        <div className={styles.personCardDivider} />
        <div className={styles.personCardBody}>
          {items.length === 0 && (
            <p className={styles.hint}>안내사항 항목을 추가하세요</p>
          )}

          {items.map((item, i) => (
            <div key={i} className={styles.noticeCard}>
              <div className={styles.itemHeader}>
                <div className={styles.itemLabelGroup}>
                  <span className={styles.itemLabel}>항목 {i + 1}</span>
                  {item.icon && ICON_LABEL_MAP[item.icon] && (
                    <span className={styles.itemTime}>· {ICON_LABEL_MAP[item.icon]}</span>
                  )}
                </div>
                <div className={styles.noticeActions}>
                  <button
                    type="button"
                    className={styles.noticeActionBtn}
                    onClick={() => moveItem(i, -1)}
                    disabled={i === 0}
                    aria-label={`항목 ${i + 1} 위로`}
                  >
                    <ChevronUp size={18} />
                  </button>
                  <button
                    type="button"
                    className={styles.noticeActionBtn}
                    onClick={() => moveItem(i, 1)}
                    disabled={i === items.length - 1}
                    aria-label={`항목 ${i + 1} 아래로`}
                  >
                    <ChevronDown size={18} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.noticeActionBtn} ${styles.noticeActionBtnDanger}`}
                    onClick={() => removeItem(i)}
                    aria-label={`항목 ${i + 1} 삭제`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className={styles.row}>
                <Select
                  label="아이콘"
                  value={item.icon ?? ''}
                  onChange={(e) => updateItem(i, 'icon', e.target.value)}
                  options={ICON_OPTIONS}
                />
                <Input
                  label="제목"
                  value={item.title}
                  onChange={(e) => updateItem(i, 'title', e.target.value)}
                  placeholder="주차 안내"
                />
              </div>
              <Textarea
                label="설명"
                value={item.description ?? ''}
                onChange={(e) => updateItem(i, 'description', e.target.value)}
                placeholder="지하 주차장 2시간 무료입니다"
                rows={2}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
